import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth, hasPagePermission } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ClipboardCheck, 
  Plus, 
  CheckCircle2, 
  Circle, 
  ChevronRight,
  ChevronLeft,
  Search,
  Calendar,
  FileText,
  Trash2,
  Loader2
} from 'lucide-react';

export const AuditsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  const canEditAudits = true; // Všichni uživatelé mají přístup

  useEffect(() => {
    fetchAudits();
  }, [page]);

  // Debounced search
  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setPage(1);
      fetchAudits();
    }, 300);
    setSearchTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const fetchAudits = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), limit: '20' });
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await api.get(`/audits?${params}`);
      setAudits(response.data.audits || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching audits:', error);
      toast.error('Chyba při načítání inspekcí');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (auditId, e) => {
    e.stopPropagation();
    if (!window.confirm('Opravdu chcete smazat tuto inspekci včetně všech propojených úkolů?')) return;
    
    try {
      await api.delete(`/audits/${auditId}`);
      // Refresh current page
      fetchAudits();
      toast.success('Inspekce smazána');
    } catch (error) {
      console.error('Error deleting audit:', error);
      toast.error('Chyba při mazání inspekce');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}.${parts[1]}.${parts[0]}`;
    }
    return dateStr;
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <ClipboardCheck className="w-7 h-7 text-emerald-600" />
              Interní inspekce
            </h1>
            <p className="text-slate-500 mt-1">
              {total > 0 ? `Celkem ${total} inspekcí` : 'Správa interních inspekcí a kontrol'}
            </p>
          </div>
          
          {canEditAudits && (
            <Button 
              onClick={() => navigate('/audity/novy')}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="new-audit-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nová inspekce
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Hledat inspekce podle čísla nebo místa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-audits-input"
            />
            {loading && searchTerm && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
            )}
          </div>
        </div>

        {/* Audits List */}
        {loading && audits.length === 0 ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : audits.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
            <ClipboardCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">
              {searchTerm ? 'Žádné inspekce nenalezeny' : 'Zatím žádné inspekce'}
            </h3>
            <p className="text-slate-400 mb-4">
              {canEditAudits ? 'Vytvořte první interní inspekci' : 'Čekejte na vytvoření inspekcí'}
            </p>
            {canEditAudits && !searchTerm && (
              <Button onClick={() => navigate('/audity/novy')} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Vytvořit inspekci
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {audits.map((audit) => (
              <div
                key={audit.id}
                onClick={() => navigate(`/audity/${audit.id}`)}
                className="bg-white rounded-xl shadow-sm border p-5 cursor-pointer hover:shadow-md transition-all"
                data-testid={`audit-card-${audit.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-800">
                        Inspekce č. {audit.audit_number}
                      </h3>
                      {audit.tasks_created && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-medium rounded-full">
                          Úkoly vytvořeny
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(audit.audit_date)}
                      </span>
                      
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        {audit.item_count || 0} nálezů
                      </span>
                      
                      {audit.item_count > 0 && (
                        <span className="flex items-center gap-1">
                          {audit.completed_count === audit.item_count ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <Circle className="w-4 h-4 text-slate-400" />
                          )}
                          {audit.completed_count}/{audit.item_count} zkontrolováno
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {canEditAudits && (
                      <button
                        onClick={(e) => handleDelete(audit.id, e)}
                        className="p-2 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
                
                {/* Progress bar */}
                {audit.item_count > 0 && (
                  <div className="mt-4">
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          audit.completed_count === audit.item_count ? 'bg-green-500' : 'bg-emerald-500'
                        }`}
                        style={{ width: `${(audit.completed_count / audit.item_count) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Předchozí
                </Button>
                
                <span className="px-4 text-sm text-slate-600">
                  Strana {page} z {totalPages}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || loading}
                >
                  Další
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AuditsPage;
